import { HttpClient } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
	FormBuilder,
	FormGroup,
	Validators,
	AbstractControl,
	ValidationErrors,
} from '@angular/forms';

import { finalize } from 'rxjs/operators';

import moment from 'moment';

import { BeneficiariesService } from '../../../../../../modules/beneficiaries/beneficiaries.service';
import {
	TransferType,
	CreateTransferDto,
} from '../../../../../../modules/beneficiaries/dtos/transfer-beneficiary.dto';
import { OrganizationDto } from '../../../../../../modules/organizations/dto/organization.dto';
import { OrganizationsService } from '../../../../../../modules/organizations/organizations.service';
import { SnackBarService } from '../../../../../../shared/components/snack-bar/snack-bar.service';

@Component({
	selector: 'app-transfer-beneficiary-modal',
	styleUrls: ['./transfer-beneficiary-modal.component.scss'],
	templateUrl: './transfer-beneficiary-modal.component.html',
})
export class TransferBeneficiaryModalComponent implements OnInit {
	@Input() beneficiaryId!: string;
	@Input() beneficiaryName!: string;
	@Output() close = new EventEmitter<void>();
	@Input() currentOrganizationId!: string;
	@Input() hasDependent = false;
	@Input() scheduledTransfer: any = null;
	@Output() success = new EventEmitter<{
		toOrganizationId: string;
		transferType: string;
	}>();

	form!: FormGroup;
	isLoading = false;
	organizations: OrganizationDto[] = [];
	selectedOrganizationName = '';
	showConfirmation = false;
	showScheduledTransferWarning = false;
	transferTypes = TransferType;

	constructor(
		private readonly fb: FormBuilder,
		private readonly http: HttpClient,
		private readonly organizationsService: OrganizationsService,
		private readonly beneficiariesService: BeneficiariesService,
		private readonly snackBarService: SnackBarService,
	) {}

	confirmTransfer(keepBenefits: boolean): void {
		this.isLoading = true;

		const formValue = this.form.value;
		const dto: CreateTransferDto = {
			keepBenefits,
			toOrganizationId: formValue.toOrganizationId,
			transferType: formValue.transferType,
		};

		if (
			formValue.transferType === TransferType.SCHEDULED &&
			formValue.scheduledDate
		) {
			dto.scheduledDate = new Date(formValue.scheduledDate).toISOString();
		}

		this.beneficiariesService
			.createTransfer({
				beneficiaryId: this.beneficiaryId,
				dto,
			})
			.pipe(finalize(() => (this.isLoading = false)))
			.subscribe({
				error: () => {
					this.snackBarService.showSnack(
						'Erro ao realizar transferência',
						'error',
					);
				},
				next: () => {
					const message =
						dto.transferType === TransferType.IMMEDIATE
							? 'Transferência realizada com sucesso'
							: 'Transferência agendada com sucesso';
					this.snackBarService.showSnack(message, 'success');
					this.success.emit({
						toOrganizationId: dto.toOrganizationId,
						transferType: dto.transferType,
					});
					this.close.emit();
				},
			});
	}

	getMaxDate(): Date {
		const sixMonthsAhead = new Date();
		sixMonthsAhead.setMonth(sixMonthsAhead.getMonth() + 6);

		return sixMonthsAhead;
	}

	getMinDate(): Date {
		const tomorrow = new Date();
		tomorrow.setDate(tomorrow.getDate() + 1);

		return tomorrow;
	}

	initForm(): void {
		this.form = this.fb.group({
			keepBenefits: [true],
			scheduledDate: [{ disabled: true, value: '' }],
			toOrganizationId: ['', Validators.required],
			transferType: [TransferType.IMMEDIATE, Validators.required],
		});

		this.form.get('transferType')?.valueChanges.subscribe((type) => {
			const scheduledDateControl = this.form.get('scheduledDate');

			if (type === TransferType.SCHEDULED) {
				if (this.scheduledTransfer) {
					this.showScheduledTransferWarning = true;
					this.form.get('transferType')?.setValue(TransferType.IMMEDIATE);

					return;
				}
				scheduledDateControl?.setValidators([
					Validators.required,
					TransferBeneficiaryModalComponent.scheduledDateRangeValidator,
				]);
				scheduledDateControl?.enable();
			} else {
				this.showScheduledTransferWarning = false;
				scheduledDateControl?.clearValidators();
				scheduledDateControl?.disable();
			}
			scheduledDateControl?.updateValueAndValidity();
		});
	}

	loadOrganizations(): void {
		this.organizationsService
			.getOrganizationsByGroup(this.currentOrganizationId)
			.subscribe({
				error: () => {
					this.snackBarService.showSnack(
						'Erro ao carregar organizações do grupo',
						'error',
					);
				},
				next: (response) => {
					this.organizations = response.data;

					if (this.organizations.length === 0) {
						this.snackBarService.showSnack(
							'Não há outras organizações disponíveis no mesmo grupo econômico',
							'warning',
						);
					}
				},
			});
	}

	ngOnInit(): void {
		this.initForm();
		this.loadOrganizations();
	}

	onClose(): void {
		this.close.emit();
	}

	onSubmit(): void {
		if (this.form.invalid) {
			return;
		}

		const selectedOrg = this.organizations.find(
			(org) => org.id === this.form.value.toOrganizationId,
		);
		this.selectedOrganizationName = selectedOrg?.name_secondary || '';
		this.showConfirmation = true;
	}

	static scheduledDateRangeValidator(
		control: AbstractControl,
	): ValidationErrors | null {
		const value = control.value;
		if (!value) return null;

		const date = moment(value);
		const minDate = moment().add(1, 'day').startOf('day');
		const maxDate = moment().add(6, 'months').endOf('day');

		if (date.isBefore(minDate) || date.isAfter(maxDate)) {
			return { dateOutOfRange: true };
		}

		return null;
	}
}
